import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuthNavigation } from '../../../shared/navigation/useAuthNavigation';
import { Alert } from 'react-native';
import moment from 'moment';

import { calculateJourneyCalories, calculateJourneyCaloriesLocal } from './api';
import { resolveJourneyPrefill } from './prefill';
import { useAuthStore } from '../../../shared/auth/store';
import { useTranslation } from '../../../shared/i18n';
import { isJourneyByApiEnabled } from '../config';
import {
  JOURNEY_ACTIVITY_OPTIONS,
  JOURNEY_AGE_MAX,
  JOURNEY_AGE_MIN,
  JOURNEY_HEIGHT_MAX,
  JOURNEY_HEIGHT_MIN,
  JOURNEY_STEP_COUNT,
  JOURNEY_WEIGHT_MAX,
  JOURNEY_WEIGHT_MIN,
  type JourneyGender,
  type JourneyGoal,
} from './config';

export function useJourneyWizard() {
  const { t } = useTranslation();
  const navigation = useAuthNavigation();

  const [step, setStep] = useState(0);
  const prefilled_ref = useRef(false);

  const [goal, setGoal] = useState<JourneyGoal>('');
  const [gender, setGender] = useState<JourneyGender>('male');
  const [birth, setBirth] = useState(new Date(2000, 0, 1));
  const [weight, setWeight] = useState(70);
  const [height, setHeight] = useState(170);
  const [activity, setActivity] = useState('moderately_active');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (prefilled_ref.current) {
      return;
    }
    prefilled_ref.current = true;
    const user = useAuthStore.getState().user as
      | (Record<string, unknown> & { preferences?: Record<string, unknown> })
      | null
      | undefined;
    if (!user) {
      return;
    }
    const prefill = resolveJourneyPrefill({
      gender: typeof user.gender === 'string' ? user.gender : null,
      date_of_birth: typeof user.date_of_birth === 'string' ? user.date_of_birth : null,
      birth_date: typeof user.birth_date === 'string' ? user.birth_date : null,
      height: (user.height as string | number | null | undefined) ?? null,
      weight: (user.weight as string | number | null | undefined) ?? null,
      preferences: user.preferences ?? null,
    });
    if (prefill.gender) {
      setGender(prefill.gender);
    }
    if (prefill.birth) {
      setBirth(prefill.birth);
    }
    if (prefill.height_cm != null) {
      setHeight(prefill.height_cm);
    }
    if (prefill.weight_kg != null) {
      setWeight(prefill.weight_kg);
    }
  }, []);

  const age = useMemo(() => {
    const m = moment(birth);
    return m.isValid() ? moment().diff(m, 'years') : 0;
  }, [birth]);

  const birth_iso = useMemo(() => moment(birth).format('YYYY-MM-DD'), [birth]);

  const can_continue = useMemo(() => {
    if (step === 0) {
      return goal !== '';
    }
    if (step === 1) {
      return (
        age >= JOURNEY_AGE_MIN &&
        age <= JOURNEY_AGE_MAX &&
        height >= JOURNEY_HEIGHT_MIN &&
        height <= JOURNEY_HEIGHT_MAX &&
        weight >= JOURNEY_WEIGHT_MIN &&
        weight <= JOURNEY_WEIGHT_MAX
      );
    }
    if (step === 2) {
      return activity !== '';
    }
    return false;
  }, [step, goal, activity, age, height, weight]);

  const set_age_years = useCallback((years: number) => {
    const y = Math.round(years);
    if (y < JOURNEY_AGE_MIN || y > JOURNEY_AGE_MAX) {
      return;
    }
    setBirth(prev => {
      const current_age = moment().diff(moment(prev), 'years');
      if (current_age === y) {
        return prev;
      }
      return new Date(new Date().getFullYear() - y, 0, 1);
    });
  }, []);

  const set_weight_kg = useCallback((kg: number) => {
    const next = Math.round(kg);
    if (next >= JOURNEY_WEIGHT_MIN && next <= JOURNEY_WEIGHT_MAX) {
      setWeight(prev => (prev === next ? prev : next));
    }
  }, []);

  const set_height_cm = useCallback((cm: number) => {
    const next = Math.round(cm);
    if (next >= JOURNEY_HEIGHT_MIN && next <= JOURNEY_HEIGHT_MAX) {
      setHeight(prev => (prev === next ? prev : next));
    }
  }, []);

  const set_gender = useCallback((next: JourneyGender) => {
    setGender(prev => (prev === next ? prev : next));
  }, []);

  const select_activity = useCallback((id: string) => {
    if (!id) {
      return;
    }
    setActivity(prev => (prev === id ? prev : id));
  }, []);

  const submit = useCallback(async () => {
    const act_opt = JOURNEY_ACTIVITY_OPTIONS.find(a => a.id === activity);
    const activity_level = act_opt?.api ?? 'Moderately Active';
    const form = {
      type: goal,
      gender,
      birth_date: birth_iso,
      age,
      height: Math.round(height),
      weight: Math.round(weight),
      activity_level,
      activity: activity_level,
      activity_id: activity,
    };

    try {
      setLoading(true);
      const { calories, message } = isJourneyByApiEnabled()
        ? await calculateJourneyCalories(form)
        : await calculateJourneyCaloriesLocal(form);
      if (calories != null) {
        (navigation as { navigate: (name: string, params?: object) => void }).navigate('JourneyResult', {
          ...form,
          calories,
          type: goal,
        });
      } else {
        const msg = message ?? t('journey.couldNotCalculate');
        Alert.alert(t('common.error'), typeof msg === 'string' ? msg : t('journey.couldNotCalculate'));
      }
    } catch (e: any) {
      const msg = e?.response?.data?.message ?? e?.response?.data?.msg ?? e?.message;
      Alert.alert(t('common.error'), typeof msg === 'string' ? msg : t('journey.somethingWentWrong'));
    } finally {
      setLoading(false);
    }
  }, [activity, goal, gender, birth_iso, age, height, weight, navigation, t]);

  const on_continue = useCallback(() => {
    if (loading || !can_continue) {
      return;
    }
    if (step < JOURNEY_STEP_COUNT - 1) {
      setStep(s => s + 1);
      return;
    }
    void submit();
  }, [loading, can_continue, step, submit]);

  const on_back_step = useCallback(() => {
    if (step > 0) {
      setStep(s => s - 1);
    }
  }, [step]);

  const select_goal = useCallback(
    (value: JourneyGoal) => {
      if (!value || loading) {
        return;
      }
      setGoal(value);
    },
    [loading],
  );

  const continue_label =
    step === JOURNEY_STEP_COUNT - 1 ? (loading ? '...' : t('journey.seeMyPlan')) : t('journey.next');

  return {
    step,
    goal,
    select_goal,
    gender,
    setGender: set_gender,
    birth,
    weight,
    height,
    activity,
    select_activity,
    loading,
    age,
    birth_iso,
    set_age_years,
    set_weight_kg,
    set_height_cm,
    can_continue,
    on_continue,
    on_back_step,
    continue_label,
  };
}
